import React, { createContext, useContext } from 'react';

import { BUTTON_HEIGHT, CHIP_HEIGHT, ICON_BUTTON_SIZE } from './constants';
import { Group, HiddenLabel, RevealGroup, Shape, TextShape } from './index.styles';
import { SkeletonRadius, SkeletonSize, SkeletonTextVariant, SkeletonTone } from './types';
import { getLineWidth } from './utils';

/** True inside a `SkeletonGroup`, so only the outermost group animates. */
const NestedContext = createContext(false);

export interface SkeletonGroupProps {
  children: React.ReactNode;
  /**
   * Announces the region to assistive technology while it loads. A page shows
   * many placeholders at once, so only the one that speaks for the whole
   * loading area should carry a label — several live regions firing together
   * read as "Loading loading loading". Groups without one are still marked
   * `aria-busy`. Ignored on a nested group, whose ancestor does the speaking.
   */
  label?: string;
  /** Takes the full height of its parent, for placeholders that fill a cell. */
  fill?: boolean;
  /** Retunes every placeholder below for the surface it sits on. */
  tone?: SkeletonTone;
  className?: string;
}

/**
 * Root of a loading region: marks itself busy, optionally announces itself,
 * and fades in after a short delay so quick responses never flash a
 * placeholder.
 */
export const SkeletonGroup = ({
  children,
  label,
  fill = false,
  tone = 'default',
  className,
}: SkeletonGroupProps) => {
  const isNested = useContext(NestedContext);

  // A nested group is part of a region its ancestor already announces, so it
  // stays silent — but it is still busy, and still carries its own tone.
  if (isNested) {
    return (
      <Group $fill={fill} $tone={tone} className={className} aria-busy='true'>
        {children}
      </Group>
    );
  }

  return (
    <NestedContext.Provider value>
      <RevealGroup
        $fill={fill}
        $tone={tone}
        className={className}
        role={label ? 'status' : undefined}
        aria-busy='true'
      >
        {label && <HiddenLabel>{label}</HiddenLabel>}
        {children}
      </RevealGroup>
    </NestedContext.Provider>
  );
};

export interface SkeletonBoxProps {
  width?: SkeletonSize;
  height?: SkeletonSize;
  radius?: SkeletonRadius;
  /** Fills the remaining space of a flex parent. */
  grow?: boolean;
  className?: string;
}

/** Rectangular placeholder — images, charts, editors, map areas. */
export const SkeletonBox = ({
  width,
  height,
  radius = 'control',
  grow = false,
  className,
}: SkeletonBoxProps) => (
  <Shape
    variant='rectangular'
    $radius={radius}
    $width={width}
    $height={height}
    $grow={grow}
    className={className}
  />
);

export interface SkeletonTextProps {
  /** Typography variant the placeholder is sized against. */
  variant?: SkeletonTextVariant;
  width?: SkeletonSize;
  grow?: boolean;
  className?: string;
}

/** A single line of text, occupying the exact line box of its variant. */
export const SkeletonText = ({
  variant = 'body2',
  width = '100%',
  grow = false,
  className,
}: SkeletonTextProps) => (
  <TextShape
    variant='text'
    $variant={variant}
    $radius='text'
    $width={width}
    $grow={grow}
    className={className}
  />
);

export interface SkeletonHeadingProps extends Omit<SkeletonTextProps, 'variant'> {
  variant?: SkeletonTextVariant;
}

/** Card and page titles. Defaults to the `h6` used by `ContentCard`. */
export const SkeletonHeading = ({ variant = 'h6', width = 160, ...rest }: SkeletonHeadingProps) => (
  <SkeletonText variant={variant} width={width} {...rest} />
);

export interface SkeletonParagraphProps {
  lines?: number;
  variant?: SkeletonTextVariant;
  /** Width of the final line, which is short by default. */
  lastLineWidth?: SkeletonSize;
  className?: string;
}

/** A run of text lines with prose-like widths and a short final line. */
export const SkeletonParagraph = ({
  lines = 3,
  variant = 'body2',
  lastLineWidth,
  className,
}: SkeletonParagraphProps) => (
  <div className={className}>
    {Array.from({ length: lines }, (_, index) => (
      <SkeletonText
        key={index}
        variant={variant}
        width={
          lastLineWidth !== undefined && index === lines - 1
            ? lastLineWidth
            : getLineWidth(index, lines)
        }
      />
    ))}
  </div>
);

export interface SkeletonCircleProps {
  size?: number;
  className?: string;
}

/** Avatars and profile icons. */
export const SkeletonCircle = ({ size = 40, className }: SkeletonCircleProps) => (
  <Shape
    variant='circular'
    $radius='circle'
    $width={size}
    $height={size}
    $grow={false}
    className={className}
  />
);

export interface SkeletonIconProps {
  size?: number;
  className?: string;
}

/** Inline icon glyph — smaller and squarer than an avatar. */
export const SkeletonIcon = ({ size = 20, className }: SkeletonIconProps) => (
  <Shape
    variant='rectangular'
    $radius='text'
    $width={size}
    $height={size}
    $grow={false}
    className={className}
  />
);

export interface SkeletonChipProps {
  width?: SkeletonSize;
  size?: keyof typeof CHIP_HEIGHT;
  className?: string;
}

export const SkeletonChip = ({ width = 72, size = 'small', className }: SkeletonChipProps) => (
  <Shape
    variant='rectangular'
    $radius='control'
    $width={width}
    $height={CHIP_HEIGHT[size]}
    $grow={false}
    className={className}
  />
);

export interface SkeletonButtonProps {
  width?: SkeletonSize;
  size?: keyof typeof BUTTON_HEIGHT;
  className?: string;
}

export const SkeletonButton = ({
  width = 104,
  size = 'medium',
  className,
}: SkeletonButtonProps) => (
  <Shape
    variant='rectangular'
    $radius='pill'
    $width={width}
    $height={BUTTON_HEIGHT[size]}
    $grow={false}
    className={className}
  />
);

export interface SkeletonIconButtonProps {
  size?: number;
  className?: string;
}

export const SkeletonIconButton = ({
  size = ICON_BUTTON_SIZE,
  className,
}: SkeletonIconButtonProps) => (
  <Shape
    variant='circular'
    $radius='circle'
    $width={size}
    $height={size}
    $grow={false}
    className={className}
  />
);

export interface SkeletonInputProps {
  width?: SkeletonSize;
  height?: number;
  grow?: boolean;
  className?: string;
}

/** Text fields, selects and other bordered inputs. */
export const SkeletonInput = ({
  width = '100%',
  height = 36,
  grow = false,
  className,
}: SkeletonInputProps) => (
  <Shape
    variant='rectangular'
    $radius='control'
    $width={width}
    $height={height}
    $grow={grow}
    className={className}
  />
);
